// useYieldFactor — Reusable hook for canonical yield factor resolution.
// All values are decimal (e.g. 0.922 = 92.2%).
// Use getYield() for math, formatYield() for display.
// Provides in-memory caching, single-item and batch lookups.

import { useState, useCallback, useRef } from 'react';
import productionService from '../services/productionService';

export const DEFAULT_YIELD = 0.85;
export const YIELD_DECIMALS = 2;

/** Named yield threshold constants (decimal). */
export const YIELD_EXCELLENT = 0.90;
export const YIELD_TARGET = 0.85;
export const YIELD_WARNING = 0.80;

/** Variance threshold (decimal) — variance above this requires supervisor approval (1 percentage point). */
export const YIELD_VARIANCE_THRESHOLD = 0.01;

/** Variance threshold (decimal) for daily transfer-out highlight (>5% diff = red). */
export const YIELD_TRANSFER_VARIANCE = 0.05;

/** Return a Tailwind text-color class based on yield level (decimal).
 *  >= 0.90 → green, >= 0.85 → yellow, < 0.85 → red. */
export const getYieldColor = (yieldDecimal: number): string => {
  if (yieldDecimal >= YIELD_EXCELLENT) return 'text-green-600 dark:text-green-400';
  if (yieldDecimal >= YIELD_TARGET) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

/** Return a Tailwind icon-color class for yield (simpler palette for icons/trend arrows).
 *  >= 0.90 → green, >= 0.80 → yellow, < 0.80 → red. */
export const getYieldIconColor = (yieldDecimal: number): string => {
  if (yieldDecimal >= YIELD_EXCELLENT) return 'text-green-500';
  if (yieldDecimal >= YIELD_WARNING) return 'text-yellow-500';
  return 'text-red-500';
};

/** Check if a yield factor source is a fallback (not directly from raw_material or inventory).
 *  Single source of truth — update here if new source types are added. */
export const isYieldFallback = (source?: string | null): boolean =>
  source === 'sibling_fallback' || source === 'default';

/** Format a decimal yield for display (e.g. 0.922 → "92.20%"). Single source of truth for yield display.
 *  If source is provided and is a fallback, appends * (e.g. "85.00%*"). */
export const formatYield = (decimal: number, decimals: number = YIELD_DECIMALS, source?: string | null): string =>
  `${(decimal * 100).toFixed(decimals)}%${isYieldFallback(source) ? '*' : ''}`;

/** Format a decimal yield variance with sign (e.g. 0.023 → "+2.30%", -0.015 → "-1.50%"). */
export const formatYieldVariance = (decimal: number, decimals: number = YIELD_DECIMALS): string => {
  const sign = decimal >= 0 ? '+' : '';
  return `${sign}${(decimal * 100).toFixed(decimals)}%`;
};

// ---------------------------------------------------------------------------
// Transfer-out yield variance helpers (single source of truth)
// Accept any object with the 3 required fields so callers don't need a
// specific type import.
// ---------------------------------------------------------------------------

/** Minimal shape needed by the yield variance helpers. */
export interface YieldVarianceItem {
  actual_weight?: number | null;
  raw_input_weight?: number | null;
  yield_factor?: number | null;   // decimal (e.g. 0.94 = 94%)
  actual_yield_percentage?: number | null; // decimal, DB-sourced sub-session yield
}

/** Compute actual yield factor as decimal, or null when data is missing.
 *  Prefers the DB-sourced sub-session yield when present. */
export const getItemActualYF = (item: YieldVarianceItem): number | null => {
  if (item.actual_yield_percentage != null) return Number(item.actual_yield_percentage);
  if (item.actual_weight == null || !item.raw_input_weight) return null;
  return Number(item.actual_weight) / Number(item.raw_input_weight);
};

/** Compute yield variance in percentage points (Act. YF − Exp. YF), or null. */
export const getItemYieldVariance = (item: YieldVarianceItem): number | null => {
  const actualYF = getItemActualYF(item);
  if (actualYF == null || item.yield_factor == null || Number(item.yield_factor) === 0) return null;
  return (actualYF - Number(item.yield_factor)) * 100;
};

/** True when yield variance exceeds the transfer-out warning threshold.
 *  @param thresholdDecimal — optional override (decimal, e.g. 0.05). Falls back to YIELD_TRANSFER_VARIANCE. */
export const isYieldVarianceWarning = (item: YieldVarianceItem, thresholdDecimal?: number): boolean => {
  const v = getItemYieldVariance(item);
  const t = (thresholdDecimal ?? YIELD_TRANSFER_VARIANCE) * 100;
  return v != null && Math.abs(v) > t;
};

/** Tailwind CSS class for a yield-variance value (percentage points).
 *  Red when |v| > threshold, yellow when non-zero, green at zero, gray when null.
 *  @param thresholdDecimal — optional override (decimal). Falls back to YIELD_TRANSFER_VARIANCE. */
export const getYieldVarianceColor = (variancePct: number | null, thresholdDecimal?: number): string => {
  if (variancePct == null) return 'text-gray-400';
  const t = (thresholdDecimal ?? YIELD_TRANSFER_VARIANCE) * 100;
  if (Math.abs(variancePct) > t) return 'text-red-600 dark:text-red-400';
  if (variancePct !== 0) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
};

/** Tailwind CSS class for the actual-YF cell, colored by how far it deviates from expected. */
export const getActualYFColor = (item: YieldVarianceItem): string => {
  const actualYF = getItemActualYF(item);
  if (actualYF == null) return 'text-gray-700 dark:text-gray-300';
  const expectedYF = Number(item.yield_factor) || 0;
  const diff = actualYF - expectedYF;
  if (Math.abs(diff) > YIELD_TRANSFER_VARIANCE) return 'text-red-600 dark:text-red-400';
  if (diff !== 0) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
};

/** Format yield variance for display: "+3.20%" / "-5.10%" / "-". */
export const formatYieldVariancePct = (variancePct: number | null): string => {
  if (variancePct == null) return '-';
  return `${variancePct > 0 ? '+' : ''}${variancePct.toFixed(2)}%`;
};

/** Hex color for yield-variance (for print/HTML templates where Tailwind isn't available). */
export const getYieldVarianceHexColor = (variancePct: number | null): string => {
  if (variancePct == null) return '#999';
  if (Math.abs(variancePct) > YIELD_TRANSFER_VARIANCE * 100) return '#dc2626';
  if (variancePct !== 0) return '#ca8a04';
  return '#16a34a';
};

interface YieldCacheEntry {
  yield_factor: number | null;
  source: string | null;
}

interface UseYieldFactorReturn {
  /** Get yield as decimal (e.g. 0.922). Use directly in math — no conversion needed. */
  getYield: (itemNumber: string) => number;
  /** Pre-fetch all material yields for a product SKU (batch). */
  fetchForProduct: (productSku: string) => Promise<void>;
  /** Fetch yield for a single item (decimal; null when unset). Pass productSku for the product-specific value. */
  fetchForItem: (itemNumber: string, productSku?: string) => Promise<number | null>;
  /** Whether a fetch is currently in progress. */
  isLoading: boolean;
  /** Default yield constant (0.85 = 85%). */
  DEFAULT_YIELD: number;
}

export function useYieldFactor(): UseYieldFactorReturn {
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Map<string, YieldCacheEntry>>(new Map());

  const getYield = useCallback((itemNumber: string): number => {
    const entry = cacheRef.current.get(itemNumber);
    return entry && entry.yield_factor != null ? entry.yield_factor : DEFAULT_YIELD;
  }, []);

  const fetchForItem = useCallback(async (itemNumber: string, productSku?: string): Promise<number | null> => {
    // Return cached value if available
    const cached = cacheRef.current.get(itemNumber);
    if (cached) return cached.yield_factor;

    setIsLoading(true);
    try {
      const data = await productionService.getYieldFactor(itemNumber, productSku);
      const entry: YieldCacheEntry = {
        yield_factor: data.yield_factor,
        source: data.source
      };
      cacheRef.current.set(itemNumber, entry);
      return entry.yield_factor;
    } catch (error) {
      return DEFAULT_YIELD;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchForProduct = useCallback(async (productSku: string): Promise<void> => {
    setIsLoading(true);
    try {
      const data = await productionService.getYieldFactorBatch(productSku);
      for (const mat of data.materials) {
        cacheRef.current.set(mat.item_number, {
          yield_factor: mat.yield_factor,
          source: mat.source
        });
      }
    } catch (error) {
      // batch yield factor fetch failed — will use defaults
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    getYield,
    fetchForProduct,
    fetchForItem,
    isLoading,
    DEFAULT_YIELD
  };
}

export default useYieldFactor;
